import {
  TAX_PARAMS,
  TAX_YEAR,
  marginalRate,
  taxFromBrackets,
  type FilingStatus,
} from "./tax-params";
import {
  num,
  safeDiv,
  str,
  type CalculatorDefinition,
  type CalculatorOutput,
} from "./types";

/**
 * Solo operator federal tax estimate: sole proprietor vs. S-corp election.
 *
 * The classic "tax-smart solo setup" question. An S-corp election splits profit
 * into a reasonable salary (which carries payroll tax) and distributions (which
 * do not), so the saving is roughly the payroll tax avoided on the distribution
 * — minus the very real cost of running payroll and filing a second return.
 *
 * Federal only. No state tax, no credits, no itemising.
 */

type Scenario = {
  seOrPayrollTax: number;
  incomeTax: number;
  qbiDeduction: number;
  taxableIncome: number;
  adminCost: number;
  total: number;
};

function computeSoleProp(
  netProfit: number,
  otherIncome: number,
  status: FilingStatus,
): Scenario {
  const seBase = Math.max(netProfit, 0) * TAX_PARAMS.seTaxableShare;

  const ssPortion =
    Math.min(seBase, TAX_PARAMS.socialSecurityWageBase) *
    TAX_PARAMS.socialSecurityRate;
  const medicarePortion = seBase * TAX_PARAMS.medicareRate;

  const addlThreshold = TAX_PARAMS.additionalMedicareThreshold[status];
  const addlMedicare =
    Math.max(seBase + otherIncome - addlThreshold, 0) *
    TAX_PARAMS.additionalMedicareRate;

  const seTax = ssPortion + medicarePortion + addlMedicare;
  // Half the SE tax is an above-the-line deduction.
  const halfSeDeduction = (ssPortion + medicarePortion) / 2;

  const agi = netProfit + otherIncome - halfSeDeduction;
  const standard = TAX_PARAMS.standardDeduction[status];

  // QBI is 20% of qualified business income, capped at 20% of taxable income
  // before the deduction itself.
  const preQbiTaxable = Math.max(agi - standard, 0);
  const qbiDeduction = Math.min(
    Math.max(netProfit - halfSeDeduction, 0) * TAX_PARAMS.qbiRate,
    preQbiTaxable * TAX_PARAMS.qbiRate,
  );

  const taxableIncome = Math.max(preQbiTaxable - qbiDeduction, 0);
  const incomeTax = taxFromBrackets(taxableIncome, status);

  return {
    seOrPayrollTax: seTax,
    incomeTax,
    qbiDeduction,
    taxableIncome,
    adminCost: 0,
    total: seTax + incomeTax,
  };
}

function computeSCorp(
  netProfit: number,
  salary: number,
  otherIncome: number,
  status: FilingStatus,
  adminCost: number,
): Scenario {
  // Payroll costs reduce the profit available to distribute. The employer half
  // of FICA is a business expense; the employee half comes out of the salary.
  const cappedSalary = Math.max(Math.min(salary, Math.max(netProfit, 0)), 0);

  const ssPortion =
    Math.min(cappedSalary, TAX_PARAMS.socialSecurityWageBase) *
    TAX_PARAMS.socialSecurityRate;
  const medicarePortion = cappedSalary * TAX_PARAMS.medicareRate;

  const addlThreshold = TAX_PARAMS.additionalMedicareThreshold[status];
  const addlMedicare =
    Math.max(cappedSalary + otherIncome - addlThreshold, 0) *
    TAX_PARAMS.additionalMedicareRate;

  const payrollTax = ssPortion + medicarePortion + addlMedicare;
  const employerHalf = (ssPortion + medicarePortion) / 2;

  // Distribution is what remains after salary, the employer's FICA share, and
  // the cost of running the entity.
  const distribution = Math.max(
    netProfit - cappedSalary - employerHalf - adminCost,
    0,
  );

  const agi = cappedSalary + distribution + otherIncome;
  const standard = TAX_PARAMS.standardDeduction[status];
  const preQbiTaxable = Math.max(agi - standard, 0);

  // Only the pass-through profit qualifies for QBI — W-2 wages do not.
  const qbiDeduction = Math.min(
    distribution * TAX_PARAMS.qbiRate,
    preQbiTaxable * TAX_PARAMS.qbiRate,
  );

  const taxableIncome = Math.max(preQbiTaxable - qbiDeduction, 0);
  const incomeTax = taxFromBrackets(taxableIncome, status);

  return {
    seOrPayrollTax: payrollTax,
    incomeTax,
    qbiDeduction,
    taxableIncome,
    adminCost,
    total: payrollTax + incomeTax + adminCost,
  };
}

export const soloTaxCalculator: CalculatorDefinition = {
  kind: "solo-tax",
  slug: "solo-tax-estimator",
  name: "Solo Tax Estimator",
  nameKo: "1인 사업자 세금 계산기",
  summary:
    "Federal tax as a sole proprietor versus an S-corp election — including whether the election actually pays for itself.",
  interpretation:
    "An S-corp election splits profit into salary and distributions; only the salary carries payroll tax, so the saving is roughly 15.3% of what you distribute. Two things kill it. First, the salary must be defensible — the IRS challenges artificially low ones, and this model does not decide what is reasonable for your work. Second, payroll processing and a second tax return typically run $1,500–$3,000 a year, which is why the election rarely pays below roughly $50k of profit.",
  disclaimer: `Rough federal estimate for tax year ${TAX_YEAR}, single earner, standard deduction, no credits or state tax. Educational only — this is not tax advice and must not be used to file. Confirm your position with a CPA or enrolled agent.`,
  relatedCaseStudySlugs: ["micro-saas-invoice-tool"],

  fields: [
    {
      id: "netProfit",
      label: "Business net profit",
      kind: "currency",
      group: "Income",
      defaultValue: 120000,
      min: 0,
      step: 5000,
      help: "Revenue minus deductible business expenses, before any owner salary.",
    },
    {
      id: "otherIncome",
      label: "Other household income",
      kind: "currency",
      group: "Income",
      defaultValue: 0,
      min: 0,
      step: 5000,
      help: "W-2 wages, a spouse's income, interest. Affects your bracket.",
    },
    {
      id: "filingStatus",
      label: "Filing status",
      kind: "select",
      group: "Income",
      defaultValue: "single",
      options: [
        { value: "single", label: "Single" },
        { value: "married_joint", label: "Married filing jointly" },
      ],
    },

    {
      id: "reasonableSalary",
      label: "Reasonable S-corp salary",
      kind: "currency",
      group: "S-corp election",
      defaultValue: 60000,
      min: 0,
      step: 5000,
      help: "What you would pay someone else to do your job. Setting this too low is the single most audited position in small-business tax.",
    },
    {
      id: "adminCost",
      label: "Annual payroll & filing cost",
      kind: "currency",
      group: "S-corp election",
      defaultValue: 2000,
      min: 0,
      step: 250,
      help: "Payroll service, bookkeeping, and the 1120-S return. Typically $1,500–$3,000.",
    },
  ],

  compute: (values) => {
    const netProfit = num(values, "netProfit");
    const otherIncome = num(values, "otherIncome");
    const salary = num(values, "reasonableSalary");
    const adminCost = num(values, "adminCost");
    const status = (str(values, "filingStatus", "single") as FilingStatus) ?? "single";

    const soleProp = computeSoleProp(netProfit, otherIncome, status);
    const sCorp = computeSCorp(netProfit, salary, otherIncome, status, adminCost);

    const savings = soleProp.total - sCorp.total;
    const soleEffective = safeDiv(soleProp.total, netProfit + otherIncome);
    const sCorpEffective = safeDiv(sCorp.total, netProfit + otherIncome);
    const salaryShare = safeDiv(salary, netProfit);

    const outputs: CalculatorOutput[] = [
      {
        id: "solePropTotal",
        label: "Sole proprietor — total federal tax",
        value: soleProp.total,
        format: "currency",
        tone: "cost",
        primary: true,
        note: "Self-employment tax plus income tax.",
      },
      {
        id: "sCorpTotal",
        label: "S-corp election — total cost",
        value: sCorp.total,
        format: "currency",
        tone: "cost",
        primary: true,
        note: "Payroll tax, income tax, and the cost of running payroll.",
      },
      {
        id: "savings",
        label: savings >= 0 ? "Annual saving from electing" : "Annual cost of electing",
        value: Math.abs(savings),
        format: "currency",
        tone: savings > 0 ? "currency" : "risk",
        primary: true,
        note:
          savings > 0
            ? "Net of payroll and filing costs."
            : "The election costs more than it saves at these numbers.",
      },

      {
        id: "seTax",
        label: "Self-employment tax (sole prop)",
        value: soleProp.seOrPayrollTax,
        format: "currency",
        tone: "cost",
        note: "15.3% on 92.35% of net profit, up to the wage base.",
      },
      {
        id: "solePropIncomeTax",
        label: "Income tax (sole prop)",
        value: soleProp.incomeTax,
        format: "currency",
        tone: "cost",
      },
      {
        id: "solePropQbi",
        label: "QBI deduction (sole prop)",
        value: soleProp.qbiDeduction,
        format: "currency",
        tone: "currency",
        note: "Up to 20% of qualified business income.",
      },
      {
        id: "payrollTax",
        label: "Payroll tax (S-corp)",
        value: sCorp.seOrPayrollTax,
        format: "currency",
        tone: "cost",
        note: "Employer and employee FICA on the salary only.",
      },
      {
        id: "sCorpIncomeTax",
        label: "Income tax (S-corp)",
        value: sCorp.incomeTax,
        format: "currency",
        tone: "cost",
      },
      {
        id: "sCorpQbi",
        label: "QBI deduction (S-corp)",
        value: sCorp.qbiDeduction,
        format: "currency",
        tone: "currency",
        note: "Applies to the distribution only — W-2 salary does not qualify.",
      },
      {
        id: "soleEffectiveRate",
        label: "Effective rate — sole prop",
        value: (soleEffective ?? 0) * 100,
        format: "percent",
        tone: "neutral",
      },
      {
        id: "sCorpEffectiveRate",
        label: "Effective rate — S-corp",
        value: (sCorpEffective ?? 0) * 100,
        format: "percent",
        tone: "neutral",
      },
      {
        id: "marginalRate",
        label: "Marginal rate on the next dollar",
        value: marginalRate(soleProp.taxableIncome, status) * 100,
        format: "percent",
        tone: "neutral",
        note: "Income tax only, before payroll tax.",
      },
      {
        id: "quarterlyPayment",
        label: "Estimated quarterly payment",
        value: soleProp.total / 4,
        format: "currency",
        tone: "cost",
        note: "Sole-proprietor total divided across four estimated payments.",
      },
    ];

    const warnings: string[] = [];
    if (netProfit > 0 && netProfit < 50_000 && savings > 0) {
      warnings.push(
        "Below roughly $50k of profit the S-corp saving is usually thin enough that the added complexity and audit surface are not worth it.",
      );
    }
    if (salaryShare != null && salaryShare < 0.3 && netProfit > 0) {
      warnings.push(
        "The salary is under 30% of profit. Low salaries relative to profit are the most commonly challenged S-corp position — a defensible figure comes from comparable market wages for your role.",
      );
    }
    if (salary > netProfit && netProfit > 0) {
      warnings.push(
        "Salary exceeds net profit, so there is nothing left to distribute and the election saves nothing.",
      );
    }
    if (savings <= 0 && netProfit > 0) {
      warnings.push(
        "At these numbers the election loses money. Raise profit, lower the salary to a still-defensible level, or stay a sole proprietor.",
      );
    }
    warnings.push(
      `Federal only — no state income tax, franchise tax, or local levies. Some states tax S-corps directly, which can erase the saving entirely.`,
    );

    return { outputs, warnings };
  },
};
